/**
 * NymblStudio DOM-inspector loader — Nymbl App Spec v1.3 §2.9.
 *
 * The studio's Build → App page can highlight and inspect elements inside the
 * iframed app. The studio and the app are different origins, so the studio
 * cannot reach into the iframe's DOM; instead it asks the app to pull in a
 * bridge script that the studio itself serves.
 *
 * This file is only the loader. The bridge (`/inspector-bridge.js`, served by
 * the studio) is what draws highlights, computes selectors and reports picks —
 * do not reimplement it here. Once injected it speaks:
 *
 *   bridge → studio : nymbl-inspector:ready | nymbl-inspector:picked
 *                     | nymbl-inspector:cancelled
 *   studio → bridge : nymbl-inspector:start | nymbl-inspector:stop
 *
 * Dev only, and inert unless iframed (see the gates below). An app that omits
 * this file simply never answers the handshake and the studio says the
 * inspector is unavailable — the inspector is a SHOULD, so nothing breaks.
 *
 * Imported for its side effect from `src/frontend.tsx`. That import is load
 * bearing twice over: without it the module is tree-shaken out and never runs,
 * and the studio's static check for §2.9 support looks for a reference to
 * `studio-inspector` from elsewhere in `src/` before it will enable Inspect.
 *
 * Two shapes here are load bearing for the "compiled out of production" MUST,
 * and all of this was checked against a real production build rather than
 * assumed. The module exports NOTHING, because an exported binding is retained
 * even once every caller is eliminated. And the origin check is a `const` arrow
 * nested in `arm()` rather than a module-level `function`, because an
 * unreferenced module-scope function is retained too, and a nested function
 * DECLARATION is hoisted so it even survives the unconditional `return` the
 * production guard collapses to. With both in place `arm()` minifies to
 * `function(){return}` and no string or host name from this file reaches the
 * bundle.
 *
 * Having no exports is also why the tests drive this through fake
 * `window`/`document` globals — which is the better test regardless: it asserts
 * that a hostile origin cannot get a script in, not that a predicate returns
 * false.
 */

/** The studio's handshake message. Any other shape is ignored. */
const INIT = "nymbl-inspector:init";

/**
 * Arms the handshake listener, once, if both gates hold.
 *
 * Gate 1 is `NODE_ENV`, checked as a bare literal comparison so the bundler can
 * fold this whole call away in a production build rather than shipping a
 * listener that never fires.
 *
 * Gate 2 is being iframed. A top-level page has no studio parent to talk to.
 *
 * `typeof window` is also checked so importing this module outside a browser
 * (a test run, any server-side render) is inert rather than a crash.
 */
function arm(): void {
  if (process.env.NODE_ENV === "production") return;
  if (typeof window === "undefined") return;
  if (window.self === window.top) return;

  /**
   * Whether a `message` event's origin may make this app inject a script.
   *
   * This is the security boundary of the whole feature: the message names the URL
   * the script is fetched from, so anything that gets past here can run arbitrary
   * code in the app's origin.
   *
   * Accepted only when, per §2.9:
   *  - the origin is loopback (local dev), or
   *  - this app's hostname is a SUBDOMAIN of the origin's — e.g. an app at
   *    `myapp.justin.nymbl.studio` accepts `justin.nymbl.studio`.
   *
   * The leading dot in that comparison is the part that matters. A bare
   * `endsWith(originHost)` would also accept `evil-justin.nymbl.studio`, since the
   * app's hostname does end with that string. Requiring `"." + originHost` forces
   * a real label boundary.
   *
   * Note this deliberately accepts ANY ancestor domain, not just the immediate
   * parent — `nymbl.studio` is trusted by `myapp.justin.nymbl.studio` too. That is
   * the rule the spec states, and it is bounded by the fact that this code only
   * exists in dev builds.
   *
   * Anything else is rejected silently: a would-be attacker learns nothing, and a
   * genuine studio would not be hitting this path.
   */
  const isTrustedStudioOrigin = (origin: string, appHostname: string): boolean => {
    if (appHostname === "") return false;

    let originHostname: string;
    try {
      // Throws on "" and on the opaque "null" origin a sandboxed frame sends,
      // both of which must fail closed rather than escape the handler.
      originHostname = new URL(origin).hostname;
    } catch {
      return false;
    }
    if (originHostname === "") return false;

    // Compared inline rather than against a module-level Set: `new Set([...])` at
    // module scope is not provably side-effect-free, so the bundler retains it and
    // the "compiled out of production entirely" requirement above quietly fails.
    // Verified against a real production build.
    if (originHostname === "localhost" || originHostname === "127.0.0.1") return true;

    return appHostname.endsWith(`.${originHostname}`);
  };

  // The bridge must be injected at most once: a second copy would register a
  // second set of listeners and double every message the studio receives.
  let injected = false;

  const onMessage = (event: MessageEvent): void => {
    if (injected) return;
    const data: unknown = event.data;
    if (typeof data !== "object" || data === null) return;
    if ((data as { type?: unknown }).type !== INIT) return;
    if (!isTrustedStudioOrigin(event.origin, window.location.hostname)) return;

    injected = true;
    // Stop listening as soon as we have acted, so the one-shot guard does not
    // depend on the closure alone.
    window.removeEventListener("message", onMessage);

    const script = document.createElement("script");
    // The origin is validated above; the studio serves the bridge at this path.
    script.src = `${event.origin}/inspector-bridge.js`;
    script.async = true;
    document.head.appendChild(script);
  };

  window.addEventListener("message", onMessage);
}

arm();
